import { AxisAlignedBoundingBox, BoxGeometry, BoxOutlineGeometry, Cartesian3, Math as CesiumMath, CylinderGeometry, EllipsoidGeometry, HeadingPitchRoll, Matrix3, Matrix4, Quaternion, VertexFormat, } from "cesium";
// ── Anchor math ─────────────────────────────────────────────────────────────
/**
 * Computes the object-space offset from the geometric center to the anchor
 * point selected by anchorType. Unscaled, unrotated.
 */
export function computeAnchorLocalOffset(anchorType, dimensions) {
    switch (anchorType) {
        case "center":
            return Cartesian3.ZERO.clone();
        case "edge-midpoint":
            return new Cartesian3(0, dimensions.y / 2, -dimensions.z / 2);
        case "top-center":
            return new Cartesian3(0, 0, dimensions.z / 2);
        case "bottom-center":
            // Cylinder axis end (P1 side): local z = -height/2
            return new Cartesian3(0, 0, -dimensions.z / 2);
        case "bottom-start":
            // Cylinder P1 end, cross-section bottom (ground contact point).
            // Derivation: R*(1,0,0) = n_unit (upward-perpendicular in world space), so
            // R^{-1}*(P1 - center) = R^T*(-(L/2)*dir - r*n_unit) = (-r, 0, -L/2).
            return new Cartesian3(-dimensions.x / 2, 0, -dimensions.z / 2);
        case "top-start":
            // Box top-edge start corner: local x = -width/2, y = +thickness/2, z = +height/2
            // Used as retaining wall rotation pivot (P1 side of top edge)
            return new Cartesian3(-dimensions.x / 2, dimensions.y / 2, dimensions.z / 2);
        case "bottom-ne":
            return new Cartesian3(dimensions.x / 2, dimensions.y / 2, -dimensions.z / 2);
        case "bottom-sw":
            return new Cartesian3(-dimensions.x / 2, -dimensions.y / 2, -dimensions.z / 2);
        case "bottom-se":
            return new Cartesian3(dimensions.x / 2, -dimensions.y / 2, -dimensions.z / 2);
        default:
            // bottom-nw: (minX, maxY, minZ) = (-w/2, d/2, -h/2)
            return new Cartesian3(-dimensions.x / 2, dimensions.y / 2, -dimensions.z / 2);
    }
}
/**
 * Computes the anchor position in local CRS from center + rotated scaled offset:
 *   anchorLocal = localCenter + R·(S·anchorOffset)
 */
export function computeAnchorLocal(anchorOffset, quaternion, scale, localCenter) {
    const scaledOffset = Cartesian3.multiplyComponents(anchorOffset, scale, new Cartesian3());
    const rotationMatrix = Matrix3.fromQuaternion(quaternion);
    const rotatedOffset = Matrix3.multiplyByVector(rotationMatrix, scaledOffset, new Cartesian3());
    return Cartesian3.add(localCenter, rotatedOffset, new Cartesian3());
}
/**
 * Inverse of `computeAnchorLocal`: derives the center from a known anchor:
 *   localCenter = anchorLocal - R·(S·anchorOffset)
 * This is the anchor-invariant used by rotate()/scale() to keep localCenter
 * in sync without a CRS round trip.
 */
export function computeLocalCenterFromAnchor(anchorLocal, anchorOffset, quaternion, scale) {
    const scaledOffset = Cartesian3.multiplyComponents(anchorOffset, scale, new Cartesian3());
    const rotatedOffset = Matrix3.multiplyByVector(Matrix3.fromQuaternion(quaternion), scaledOffset, new Cartesian3());
    return Cartesian3.subtract(anchorLocal, rotatedOffset, new Cartesian3());
}
/**
 * Builds the local CRS frame from localOrigin and the points 1 m along each axis, converted to ECEF by the CRS.
 * @param ecefSamples - The 4 points of localFrameSamplePoints(localOrigin) converted to ECEF
 * @param ecefOrigin - Known ECEF position of the origin (e.g. a saved wgs84Center); defaults to ecefSamples[0]
 */
export function localFrameFromEcefSamples(localOrigin, ecefSamples, ecefOrigin = ecefSamples[0]) {
    const [origin, ex, ey, ez] = ecefSamples;
    const axis = (p) => Cartesian3.subtract(p, origin, new Cartesian3());
    const x = axis(ex);
    const y = axis(ey);
    const z = axis(ez);
    return {
        localOrigin: Cartesian3.clone(localOrigin),
        toEcef: new Matrix4(x.x, y.x, z.x, ecefOrigin.x, x.y, y.y, z.y, ecefOrigin.y, x.z, y.z, z.z, ecefOrigin.z, 0, 0, 0, 1),
    };
}
/** Returns the 4 local points (origin and 1 m along each axis) to sample for localFrameFromEcefSamples. */
export function localFrameSamplePoints(localOrigin) {
    return [
        Cartesian3.ZERO,
        Cartesian3.UNIT_X,
        Cartesian3.UNIT_Y,
        Cartesian3.UNIT_Z,
    ].map((step) => Cartesian3.add(localOrigin, step, new Cartesian3()));
}
/**
 * The single place that composes a simple structure's modelMatrix.
 *   modelMatrix = frame.toEcef · T(localCenter - frame.localOrigin) · R · S
 * @returns Matrix mapping center-origin object vertices to ECEF
 */
export function composeModelMatrix(frame, localCenter, quaternion, scale) {
    const localTransform = Matrix4.fromTranslationQuaternionRotationScale(Cartesian3.subtract(localCenter, frame.localOrigin, new Cartesian3()), quaternion, scale, new Matrix4());
    return Matrix4.multiply(frame.toEcef, localTransform, new Matrix4());
}
/**
 * Compute the local Z shift that preserves the bottom-edge Z position
 * after a dimension or rotation change.
 *
 * Currently used only for retaining-wall (anchorType === "top-start"),
 * which always has pitch === 0. The formula `height * cos(roll)` is
 * therefore sufficient; a general pitch≠0 term is intentionally omitted.
 */
export function computeBottomZCorrection(attrs, height, quaternion, anchorLocal) {
    if (attrs.bottomZ === undefined || attrs.anchorType !== "top-start") {
        return undefined;
    }
    const hpr = HeadingPitchRoll.fromQuaternion(quaternion);
    const desiredAnchorZ = attrs.bottomZ + height * Math.cos(hpr.roll);
    const dz = desiredAnchorZ - anchorLocal.z;
    if (Math.abs(dz) <= 1e-10)
        return undefined;
    return { localDz: dz };
}
/**
 * Horizontal bearing (degrees) of a rotated object axis, in the HPR heading
 * convention of the two-point builders:
 *   axis "z" (pipe):  heading = atan2(dirY, -dirX)   — see buildPipeFromTwoPoints
 *   axis "x" (wall):  heading = atan2(-dirY, dirX)   — see buildRetainingWallFromTwoPoints
 * Euler-decomposition free, so it stays well-defined for steep/downward pipes.
 * @returns Bearing in degrees, or null when the axis is (near-)vertical
 */
export function axisHorizontalBearingDegrees(quaternion, axis) {
    const m = Matrix3.fromQuaternion(quaternion);
    const dir = Matrix3.getColumn(m, axis === "x" ? 0 : 2, new Cartesian3());
    const horizontal = Math.sqrt(dir.x * dir.x + dir.y * dir.y);
    if (horizontal < 1e-10)
        return null;
    return axis === "z"
        ? CesiumMath.toDegrees(Math.atan2(dir.y, -dir.x))
        : CesiumMath.toDegrees(Math.atan2(-dir.y, dir.x));
}
/**
 * Second endpoint of a two-point structure from its anchor (= P1) and the
 * current rotation state:  p2 = p1 + normalize(R·unitAxis) · (dim · scale).
 * Signed extent: a negative scale flips the endpoint to the opposite side.
 * Quaternion-axis based — no Euler decomposition, hence no gimbal ambiguity.
 */
export function computeLocalAxisEndpoint(anchorLocal, quaternion, scale, dimensions, axis) {
    const m = Matrix3.fromQuaternion(quaternion);
    const dir = Matrix3.getColumn(m, axis === "x" ? 0 : 2, new Cartesian3());
    Cartesian3.normalize(dir, dir);
    const extent = axis === "x" ? dimensions.x * scale.x : dimensions.z * scale.z;
    return Cartesian3.add(anchorLocal, Cartesian3.multiplyByScalar(dir, extent, new Cartesian3()), new Cartesian3());
}
// ── Rotation-degree conversions (UI boundary) ───────────────────────────────
/**
 * Converts degrees to Quaternion
 * @param xDeg - Roll angle in degrees (X-axis)
 * @param yDeg - Pitch angle in degrees (Y-axis)
 * @param zDeg - Heading angle in degrees (Z-axis)
 * @returns Quaternion
 */
export function degreesToQuaternion(xDeg, yDeg, zDeg) {
    // Cesium order: Z (heading), Y (pitch), X (roll)
    const hpr = new HeadingPitchRoll(CesiumMath.toRadians(zDeg), // heading (Z-axis)
    CesiumMath.toRadians(yDeg), // pitch (Y-axis)
    CesiumMath.toRadians(xDeg));
    return Quaternion.fromHeadingPitchRoll(hpr);
}
/**
 * Converts Quaternion to degrees, finding the values closest to the reference angles.
 * This helps avoid sudden jumps in values due to Euler angle aliasing (e.g. 0 -> 360).
 *
 * Near gimbal lock (|pitch| ≈ 90°), heading and roll from
 * HeadingPitchRoll.fromQuaternion are degenerate.  In this region we extract
 * heading AND pitch directly from the rotation matrix's Z-column (the object's
 * local Z-axis direction), which is always well-defined:
 *   heading = atan2(zCol.y, -zCol.x)
 *   pitch   = acos(zCol.z)           → range [0°, 180°]
 *   roll    = 0
 * This gives a stable HPR that correctly encodes both upward AND downward slopes
 * without requiring the caller to handle roll=180° equivalences.
 *
 * **Caveat**: When the Z-column branch activates (any non-zero pitch), roll is
 * forced to 0. This is correct for pipes but means shapes that encode
 * meaningful roll (e.g. retaining-wall gradient) must keep pitch === 0 to
 * preserve their roll value through this function.
 *
 * @param quaternion - Quaternion to convert
 * @param reference - Reference angles in degrees (optional)
 * @returns Rotation angles in degrees closest to reference
 */
export function quaternionToClosestDegrees(quaternion, reference) {
    const hpr = HeadingPitchRoll.fromQuaternion(quaternion);
    let r = CesiumMath.toDegrees(hpr.roll);
    let p = CesiumMath.toDegrees(hpr.pitch);
    let h = CesiumMath.toDegrees(hpr.heading);
    // Extract heading and pitch from the rotation matrix Z-column instead of
    // relying solely on fromQuaternion (which uses asin → pitch ∈ [-90°, +90°]
    // and suffers from gimbal lock near ±90°).
    // The Z-column method returns pitch via acos ∈ [0°, 180°], which matches
    // the convention used by buildPipeFromTwoPoints / computePipeP2FromRotation.
    // Guard: only enter when |pitch| > 0.001° to avoid fully-vertical pipes
    // where heading is inherently undefined.  A previous threshold of 1° caused
    // misses when fromQuaternion returned pitch ≈ 1° for near-vertical downward
    // pipes (the gimbal-lock equivalent of acos-pitch ≈ 179°).
    // The inner sinP > 1e-10 guard already prevents heading instability for
    // small pitch values.
    // Also guard on |roll| ≈ 0 or ≈ ±180 (pipe-like rotation) because this
    // path forces roll = 0 and would break shapes with meaningful roll.
    if (Math.abs(p) > 1e-3 &&
        (Math.abs(r) < 1 || Math.abs(Math.abs(r) - 180) < 1)) {
        const r3 = Matrix3.fromQuaternion(quaternion);
        const zCol = Matrix3.getColumn(r3, 2, new Cartesian3());
        const sinP = Math.sqrt(zCol.x * zCol.x + zCol.y * zCol.y);
        if (sinP > 1e-10) {
            h = CesiumMath.toDegrees(Math.atan2(zCol.y, -zCol.x));
        }
        // acos gives pitch in [0°, 180°]: slightly above horizontal → < 90°,
        // slightly below horizontal → > 90°.  This matches the convention
        // used by buildPipeFromTwoPoints and computePipeP2FromRotation.
        p = CesiumMath.toDegrees(Math.acos(Math.max(-1, Math.min(1, zCol.z))));
        r = 0;
    }
    if (!reference) {
        return { x: r, y: p, z: h };
    }
    // Helper to find closest angle by adding multiples of 360
    const adjust = (val, ref) => {
        const diff = ref - val;
        const turns = Math.round(diff / 360);
        return val + turns * 360;
    };
    // Candidate 1: Canonical HPR (possibly corrected for gimbal lock above)
    const c1 = {
        x: adjust(r, reference.x),
        y: adjust(p, reference.y),
        z: adjust(h, reference.z),
    };
    // Candidate 2: Alternative HPR (gimbal lock flip equivalent)
    // (r + 180, 180 - p, h + 180)
    const r2 = r + 180;
    const p2 = 180 - p;
    const h2 = h + 180;
    const c2 = {
        x: adjust(r2, reference.x),
        y: adjust(p2, reference.y),
        z: adjust(h2, reference.z),
    };
    // Calculate distances
    const distSq1 = (c1.x - reference.x) ** 2 +
        (c1.y - reference.y) ** 2 +
        (c1.z - reference.z) ** 2;
    const distSq2 = (c2.x - reference.x) ** 2 +
        (c2.y - reference.y) ** 2 +
        (c2.z - reference.z) ** 2;
    return distSq1 <= distSq2 ? c1 : c2;
}
/**
 * Gets OBB (Oriented Bounding Box) vertices from ShapeConfig.
 * Creates a box geometry from dimensions*scale, then applies rotation to each
 * vertex. The returned vertices are in local ENU (East-North-Up) space.
 * See solid-mesh-helper history for visualization usage examples.
 */
export function getOBBVerticesFromShapeConfig(shapeConfig) {
    const scaledDimensions = new Cartesian3(Math.abs(shapeConfig.dimensions.x * shapeConfig.scale.x), Math.abs(shapeConfig.dimensions.y * shapeConfig.scale.y), Math.abs(shapeConfig.dimensions.z * shapeConfig.scale.z));
    const boxOutline = BoxOutlineGeometry.fromDimensions({
        dimensions: scaledDimensions,
    });
    const geometry = BoxOutlineGeometry.createGeometry(boxOutline);
    if (!geometry?.attributes?.position?.values) {
        throw new Error("Failed to create BoxOutlineGeometry");
    }
    const rotationMatrix = Matrix4.fromRotationTranslation(Matrix3.fromQuaternion(shapeConfig.quaternion), Cartesian3.ZERO);
    // Apply rotation to each vertex
    const values = geometry.attributes.position.values;
    const vertices = new Float64Array(values.length);
    for (let i = 0; i < values.length; i += 3) {
        const p = new Cartesian3(values[i], values[i + 1], values[i + 2]);
        const rotated = Matrix4.multiplyByPoint(rotationMatrix, p, new Cartesian3());
        vertices[i] = rotated.x;
        vertices[i + 1] = rotated.y;
        vertices[i + 2] = rotated.z;
    }
    return { vertices, boxOutline, rotationMatrix };
}
/**
 * Computes local AABB from rotated OBB vertices. The AABB is aligned to
 * local ENU axes; the Renderer applies only ENU (no rotation) when drawing it.
 */
export function computeLocalAABBFromVertices(vertices) {
    if (vertices.length < 3) {
        throw new Error("vertices must have at least one vertex");
    }
    const points = [];
    for (let i = 0; i < vertices.length; i += 3) {
        points.push(new Cartesian3(vertices[i], vertices[i + 1], vertices[i + 2]));
    }
    const aabb = AxisAlignedBoundingBox.fromPoints(points);
    // AABB dimensions
    const dimensions = Cartesian3.subtract(aabb.maximum, aabb.minimum, new Cartesian3());
    // BoxOutlineGeometry with AABB dimensions
    const boxOutline = BoxOutlineGeometry.fromDimensions({ dimensions });
    // Translation to AABB center
    const aabbCenterTranslation = Matrix4.fromTranslation(aabb.center, new Matrix4());
    return { aabb, boxOutline, aabbCenterTranslation };
}
/** Sphere tessellation used by extractShapeGeometry. */
const SPHERE_SLICE_PARTITIONS = 32;
const SPHERE_STACK_PARTITIONS = 16;
/** Cylinder tessellation used by extractShapeGeometry. */
const CYLINDER_SLICES = 32;
/**
 * Extracts the unit-shape vertex/index buffers for a shape config, together
 * with its transform. Pure: depends only on the config values.
 * @returns IndexedBufferWithTransform or undefined if the shape is unsupported
 */
export function extractShapeGeometry(config) {
    const geometry = createUnitShapeGeometry(config);
    if (!geometry)
        return undefined;
    return {
        vertices: Array.from(geometry.attributes.position.values),
        indices: Array.from(geometry.indices),
        transform: {
            origin: config.localCenter.clone(),
            translation: config.translation.clone(),
            rotation: config.quaternion.clone(),
            scale: config.scale.clone(),
        },
    };
}
/** Builds the untransformed Cesium geometry for a shape config. */
function createUnitShapeGeometry(config) {
    switch (config.shapeType) {
        case "box": {
            const geometry = BoxGeometry.createGeometry(BoxGeometry.fromDimensions({
                dimensions: config.dimensions,
                vertexFormat: VertexFormat.POSITION_ONLY,
            }));
            if (!geometry)
                throw new Error("Failed to create box geometry");
            return geometry;
        }
        case "sphere": {
            // Convert dimensions (diameter) to radii by multiplying by 0.5
            const radii = Cartesian3.multiplyByScalar(config.dimensions, 0.5, new Cartesian3());
            const geometry = EllipsoidGeometry.createGeometry(new EllipsoidGeometry({
                radii,
                slicePartitions: SPHERE_SLICE_PARTITIONS,
                stackPartitions: SPHERE_STACK_PARTITIONS,
                vertexFormat: VertexFormat.POSITION_ONLY,
            }));
            if (!geometry)
                throw new Error("Failed to create sphere geometry");
            return geometry;
        }
        case "cylinder": {
            const radiusX = Math.abs(config.dimensions.x) * 0.5;
            const radiusY = Math.abs(config.dimensions.y) * 0.5;
            // Cesium cylinders are circular; average X/Y diameters to keep it stable.
            const radius = (radiusX + radiusY) * 0.5;
            const length = Math.abs(config.dimensions.z);
            const geometry = CylinderGeometry.createGeometry(new CylinderGeometry({
                length,
                topRadius: radius,
                bottomRadius: radius,
                slices: CYLINDER_SLICES,
                vertexFormat: VertexFormat.POSITION_ONLY,
            }));
            if (!geometry)
                throw new Error("Failed to create cylinder geometry");
            return geometry;
        }
        default:
            console.error(`No extractor for shape type: ${config.shapeType}`);
            return undefined;
    }
}
/**
 * Applies transform to positions: scale -> rotation -> translation + origin
 * @param buffer - IndexedBufferWithTransform with positions and transform
 * @returns IGeometry with transformed positions
 */
export function applyTransformToPositions(buffer) {
    const { vertices, indices, transform } = buffer;
    const { origin, rotation, scale, translation } = transform;
    // Offset = origin + translation
    const offset = Cartesian3.add(origin, translation, new Cartesian3());
    // Build transform matrix: scale -> rotation -> translation
    const matrix = Matrix4.fromTranslationQuaternionRotationScale(offset, rotation, scale, new Matrix4());
    const transformedPositions = [];
    const vertex = new Cartesian3();
    const transformed = new Cartesian3();
    for (let i = 0; i < vertices.length; i += 3) {
        Cartesian3.fromElements(vertices[i], vertices[i + 1], vertices[i + 2], vertex);
        Matrix4.multiplyByPoint(matrix, vertex, transformed);
        transformedPositions.push(transformed.x, transformed.y, transformed.z);
    }
    const isMirrored = scale.x * scale.y * scale.z < 0;
    const transformedIndices = [...indices];
    if (isMirrored) {
        for (let i = 0; i + 2 < transformedIndices.length; i += 3) {
            const tmp = transformedIndices[i + 1];
            transformedIndices[i + 1] = transformedIndices[i + 2];
            transformedIndices[i + 2] = tmp;
        }
    }
    return {
        vertices: transformedPositions,
        indices: transformedIndices,
    };
}
//# sourceMappingURL=solid-mesh-geometry.js.map